import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Clock, Sparkles, Send, Settings, CreditCard, RefreshCw, ExternalLink,
  MapPin, Landmark, FileCheck, X, ListChecks, Target, LogOut, Heart,
  CalendarDays, Inbox, ThumbsUp, ThumbsDown, Eye, FileText, Loader2, Star, Lock,
  Trophy, Building2, TrendingUp, Mail, Copy, Pencil, Check, Globe,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Logo } from "@/components/Navbar";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ReviewDialog, AdminReviews } from "@/components/Reviews";
import CalendarView from "@/components/CalendarView";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { fmtDate, daysLeft, scoreColor, fmtEuro } from "@/lib/format";

const STATUS_BADGE = {
  viewed: { label: "Consulté", cls: "text-slate-500 bg-slate-100 border-slate-200", icon: Eye },
  interested: { label: "Ça m'intéresse", cls: "text-emerald-700 bg-emerald-50 border-emerald-200", icon: ThumbsUp },
  applied: { label: "Candidaté", cls: "text-blue-700 bg-blue-50 border-blue-200", icon: FileCheck },
  won: { label: "Marché gagné", cls: "text-amber-700 bg-amber-50 border-amber-300", icon: Trophy },
};

function TenderModal({ idweb, onClose, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fav, setFav] = useState(false);
  const [status, setStatus] = useState(null);
  const [memo, setMemo] = useState(null);
  const [memoLoading, setMemoLoading] = useState(false);
  const [wonMode, setWonMode] = useState(false);
  const [wonAmt, setWonAmt] = useState("");
  const [competitors, setCompetitors] = useState(null);
  const [compStats, setCompStats] = useState(null);
  const [compLoading, setCompLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/tenders/${idweb}`).then(({ data }) => {
      setData(data); setFav(data.favorite); setStatus(data.user_status); setMemo(data.memo); setLoading(false);
      setWonAmt(data.won_amount ? String(data.won_amount) : "");
    }).catch(() => { setLoading(false); toast.error("Impossible de charger ce marché."); });
  }, [idweb]);

  const toggleFav = async () => {
    const { data } = await api.post(`/favorites/${idweb}`);
    setFav(data.favorite);
    toast[data.favorite ? "success" : "info"](data.favorite ? "Ajouté à vos favoris" : "Retiré des favoris");
    onChange?.();
  };
  const setSt = async (s, amount) => {
    await api.post(`/tenders/${idweb}/status`, { status: s, amount });
    setStatus(s);
    if (s === "won") toast.success("Félicitations 🎉 marché enregistré comme gagné");
    else toast.success(s === "interested" ? "Noté, nous affinerons vos prochains envois" : s === "applied" ? "Marqué comme candidaté" : "Nous éviterons ce type de marché");
    onChange?.();
    if (s === "dismissed") onClose();
  };

  const loadCompetitors = async () => {
    setCompLoading(true);
    try {
      const { data } = await api.get(`/tenders/${idweb}/competitors`);
      setCompetitors(data.competitors || []);
      setCompStats(data.stats);
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Analyse réservée au Premium" : "Analyse indisponible pour le moment.");
    } finally { setCompLoading(false); }
  };
  const genMemo = async () => {
    setMemoLoading(true);
    try {
      const { data } = await api.post(`/tenders/${idweb}/memo`);
      setMemo(data.memo);
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Fonction réservée à la formule Premium" : "Génération indisponible.");
    } finally { setMemoLoading(false); }
  };

  const a = data?.analysis;
  const dl = daysLeft(data?.date_limite);
  const isPremium = ["premium", "trialing", "trial"].includes(data?.plan);

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0" data-testid="tender-modal">
      <DialogTitle className="sr-only">Détail du marché public</DialogTitle>
      <DialogDescription className="sr-only">Fiche détaillée du marché public sélectionné</DialogDescription>
      {loading ? (
        <div className="flex flex-col items-center justify-center py-24">
          <div className="w-9 h-9 border-2 border-primary border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-sm text-slate-500 font-mono">Nous préparons votre fiche…</p>
        </div>
      ) : data && (
        <div>
          <div className="p-6 border-b border-border bg-slate-50">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={`text-xs font-mono font-bold px-2.5 py-1 rounded border ${scoreColor(a?.score)}`}>PERTINENCE {a?.score}%</span>
                  {dl != null && dl >= 0 && <span className="text-xs font-mono text-amber-600 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded flex items-center gap-1"><Clock className="w-3 h-3" /> J-{dl}</span>}
                  {status && STATUS_BADGE[status] && <span className={`text-xs font-mono px-2.5 py-1 rounded border ${STATUS_BADGE[status].cls}`}>{STATUS_BADGE[status].label}</span>}
                </div>
                <h2 className="font-display text-xl font-bold text-slate-900 leading-snug">{data.objet}</h2>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={toggleFav} className="p-1.5 hover:bg-slate-200 rounded-lg" data-testid="tender-fav-btn">
                  <Heart className={`w-5 h-5 ${fav ? "fill-red-500 text-red-500" : "text-slate-400"}`} />
                </button>
                <button onClick={onClose} className="p-1.5 hover:bg-slate-200 rounded-lg" data-testid="tender-modal-close"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-600">
              <span className="flex items-center gap-1.5"><Landmark className="w-4 h-4 text-slate-400" /> {data.acheteur}</span>
              <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-slate-400" /> {(data.departements || []).join(", ") || "France"}</span>
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-slate-400" /> Limite : {fmtDate(data.date_limite)}</span>
            </div>
          </div>

          <div className="p-6 space-y-5">
            <div className="rounded-xl bg-blue-50 border border-blue-100 p-4">
              <div className="flex items-center gap-2 text-primary font-semibold text-sm mb-1.5"><Sparkles className="w-4 h-4" /> Le marché en clair</div>
              <p className="text-sm text-slate-700 leading-relaxed">{a?.summary}</p>
            </div>
            <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4">
              <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm mb-1.5"><Target className="w-4 h-4" /> Pourquoi ce marché est pour vous</div>
              <p className="text-sm text-slate-700 leading-relaxed">{a?.why_for_you}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-xl border border-border p-4">
                <p className="text-xs font-mono uppercase tracking-wide text-slate-400 mb-1">Budget estimé</p>
                <p className="text-sm font-semibold text-slate-900">{a?.budget_estimate}</p>
              </div>
              <div className="rounded-xl border border-border p-4">
                <p className="text-xs font-mono uppercase tracking-wide text-slate-400 mb-1">Critères d'attribution</p>
                <p className="text-sm font-semibold text-slate-900">{a?.criteria}</p>
              </div>
            </div>
            {a?.checklist?.length > 0 && (
              <div className="rounded-xl border border-border p-4">
                <div className="flex items-center gap-2 text-slate-900 font-semibold text-sm mb-2"><ListChecks className="w-4 h-4 text-primary" /> Pièces à préparer</div>
                <ul className="space-y-1.5">
                  {a.checklist.map((c, i) => <li key={i} className="flex gap-2 text-sm text-slate-600"><FileCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> {c}</li>)}
                </ul>
              </div>
            )}

            {/* Premium memo */}
            <div className="rounded-xl border border-dashed border-primary/40 bg-primary/5 p-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-primary font-semibold text-sm"><FileText className="w-4 h-4" /> Mémoire technique prérédigé {!isPremium && <span className="text-xs font-normal text-slate-400">(Premium)</span>}</div>
                {!memo && (
                  <Button size="sm" variant="outline" onClick={isPremium ? genMemo : () => toast.info("Passez en Premium pour générer votre mémoire")} disabled={memoLoading} className="rounded-full" data-testid="tender-memo-btn">
                    {memoLoading ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Rédaction…</> : "Générer un premier jet"}
                  </Button>
                )}
              </div>
              {memo && <div className="mt-3 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed bg-white rounded-lg p-4 border border-border max-h-72 overflow-y-auto" data-testid="tender-memo-content">{memo}</div>}
            </div>

            {/* Premium competitive analysis */}
            <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50/60 p-4" data-testid="competitor-block">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-amber-700 font-semibold text-sm"><TrendingUp className="w-4 h-4" /> Analyse concurrentielle {!isPremium && <span className="text-xs font-normal text-slate-400">(Premium)</span>}</div>
                {competitors === null && (
                  <Button size="sm" variant="outline" onClick={isPremium ? loadCompetitors : () => toast.info("Passez en Premium pour l'analyse concurrentielle")} disabled={compLoading} className="rounded-full" data-testid="competitor-load-btn">
                    {compLoading ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Analyse…</> : "Voir qui gagne ces marchés"}
                  </Button>
                )}
              </div>
              {isPremium && competitors !== null && (
                <div className="mt-3 space-y-3" data-testid="competitor-results">
                  {compStats && (
                    <div className="rounded-lg bg-white border border-amber-200 p-3 text-sm text-slate-700">
                      Sur <b>{compStats.count}</b> marchés similaires récemment attribués, le montant <b>médian</b> est de <b>{fmtEuro(compStats.median)}</b> <span className="text-slate-400">(de {fmtEuro(compStats.min)} à {fmtEuro(compStats.max)})</span>. Un repère concret pour fixer votre prix.
                    </div>
                  )}
                  {competitors.length === 0 && <p className="text-sm text-slate-500">Aucun marché comparable trouvé dans les données publiques pour l'instant.</p>}
                  {competitors.map((c, i) => (
                    <div key={i} className="flex items-start justify-between gap-3 bg-white rounded-lg border border-border p-3" data-testid="competitor-row">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{c.titulaire_nom}</p>
                        <p className="text-xs text-slate-500 line-clamp-1">{c.objet}</p>
                        <p className="text-xs text-slate-400 mt-0.5">Notifié le {fmtDate(c.date_notification)}</p>
                      </div>
                      <span className="text-sm font-mono font-bold text-emerald-700 whitespace-nowrap shrink-0">{fmtEuro(c.montant)}</span>
                    </div>
                  ))}
                  <p className="text-xs text-slate-400">Source : DECP — data.economie.gouv.fr, Licence Ouverte Etalab 2.0.</p>
                </div>
              )}
            </div>

            {/* Status actions */}
            <div className="rounded-xl border border-border p-4">
              <p className="text-xs font-mono uppercase tracking-wide text-slate-400 mb-2">Votre suivi</p>
              <div className="flex flex-wrap gap-2">
                <Button variant={status === "interested" ? "default" : "outline"} size="sm" onClick={() => setSt("interested")} className="rounded-full" data-testid="tender-interested-btn"><ThumbsUp className="w-4 h-4 mr-1.5" /> Ça m'intéresse</Button>
                <Button variant={status === "applied" ? "default" : "outline"} size="sm" onClick={() => setSt("applied")} className="rounded-full" data-testid="tender-applied-btn"><FileCheck className="w-4 h-4 mr-1.5" /> J'ai candidaté</Button>
                <Button variant={status === "won" ? "default" : "outline"} size="sm" onClick={() => setWonMode((v) => !v)} className="rounded-full" data-testid="tender-won-btn"><Trophy className="w-4 h-4 mr-1.5" /> Marché gagné</Button>
                <Button variant="ghost" size="sm" onClick={() => setSt("dismissed")} className="rounded-full text-slate-500" data-testid="tender-dismiss-btn"><ThumbsDown className="w-4 h-4 mr-1.5" /> Pas pour moi</Button>
              </div>
              {(wonMode || status === "won") && (
                <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="won-amount-row">
                  <span className="text-sm text-slate-600">Montant du marché remporté :</span>
                  <input type="number" value={wonAmt} onChange={(e) => setWonAmt(e.target.value)} placeholder="€ HT" className="w-36 rounded-lg border border-border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" data-testid="won-amount-input" />
                  <Button size="sm" className="rounded-full" onClick={() => setSt("won", wonAmt ? Number(wonAmt) : null)} data-testid="won-confirm-btn">{status === "won" ? "Mettre à jour" : "Valider le gain 🎉"}</Button>
                </div>
              )}
            </div>

            <a href={data.url} target="_blank" rel="noopener noreferrer" data-testid="tender-official-link">
              <Button className="w-full rounded-full">Voir l'avis officiel et télécharger le dossier <ExternalLink className="w-4 h-4 ml-1.5" /></Button>
            </a>
            <p className="text-xs text-slate-400 text-center">Source : BOAMP / DILA, Licence Ouverte Etalab 2.0. Le dossier se télécharge sur la plateforme de l'acheteur.</p>
          </div>
        </div>
      )}
    </DialogContent>
  );
}

function TenderCard({ t, onOpen }) {
  const dl = daysLeft(t.date_limite);
  const sb = t.user_status && STATUS_BADGE[t.user_status];
  return (
    <button onClick={() => onOpen(t.idweb)} data-testid={`tender-card-${t.idweb}`} className="text-left rounded-2xl bg-white border border-border p-5 hover-lift">
      <div className="flex items-center justify-between mb-3">
        {t.match_score != null ? (
          <span className={`text-xs font-mono font-bold px-2.5 py-1 rounded border ${scoreColor(t.match_score)}`}>PERTINENCE {t.match_score}%</span>
        ) : <span />}
        <div className="flex items-center gap-1.5">
          {t.favorite && <Heart className="w-4 h-4 fill-red-500 text-red-500" />}
          {dl != null && dl >= 0 ? <span className="text-xs font-mono text-amber-600 flex items-center gap-1"><Clock className="w-3 h-3" /> J-{dl}</span> : <span className="text-xs font-mono text-slate-400">Date à vérifier</span>}
        </div>
      </div>
      <h3 className="font-semibold text-slate-900 leading-snug line-clamp-2 mb-2">{t.objet}</h3>
      <p className="text-sm text-slate-500 flex items-center gap-1.5"><Landmark className="w-3.5 h-3.5" /> {t.acheteur}</p>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
        <span className="text-xs text-slate-400 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {(t.departements || []).join(", ") || "France"}</span>
        {sb ? <span className={`text-xs font-mono px-2 py-0.5 rounded border ${sb.cls}`}>{sb.label}</span>
            : <span className="text-xs font-semibold text-primary flex items-center gap-1">Voir la fiche <Sparkles className="w-3.5 h-3.5" /></span>}
      </div>
    </button>
  );
}

function BuyerCard({ b, onContact }) {
  return (
    <div className="rounded-2xl bg-white border border-border p-5 hover-lift flex flex-col" data-testid="buyer-card">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-mono font-bold px-2.5 py-1 rounded border text-emerald-700 bg-emerald-50 border-emerald-200">{fmtEuro(b.montant)}</span>
        <span className="text-xs font-mono text-slate-400">Notifié le {fmtDate(b.date_notification)}</span>
      </div>
      <h3 className="font-semibold text-slate-900 leading-snug line-clamp-2 mb-2">{b.objet}</h3>
      <p className="text-sm text-slate-500 flex items-center gap-1.5"><Landmark className="w-3.5 h-3.5" /> {b.acheteur_nom}</p>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
        <span className="text-xs text-slate-400 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Dép. {b.departement}</span>
        {b.annuaire_url && <a href={b.annuaire_url} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-primary flex items-center gap-1" data-testid="buyer-annuaire-link">Voir l'acheteur <ExternalLink className="w-3.5 h-3.5" /></a>}
      </div>
      <Button variant="outline" size="sm" onClick={() => onContact(b)} className="w-full rounded-full mt-3" data-testid="buyer-contact-btn"><Mail className="w-4 h-4 mr-1.5" /> Contacter l'acheteur</Button>
    </div>
  );
}

function TedCard({ n }) {
  return (
    <div className="rounded-2xl bg-white border border-border p-5 hover-lift" data-testid="ted-card">
      <div className="flex items-center justify-between mb-3">
        {n.value ? <span className="text-xs font-mono font-bold px-2.5 py-1 rounded border text-indigo-700 bg-indigo-50 border-indigo-200">{fmtEuro(n.value)}</span> : <span className="text-xs font-mono text-slate-400">Montant non communiqué</span>}
        {n.deadline && <span className="text-xs font-mono text-slate-400">Limite {fmtDate(n.deadline)}</span>}
      </div>
      <h3 className="font-semibold text-slate-900 leading-snug line-clamp-2 mb-2">{n.title}</h3>
      <p className="text-sm text-slate-500 flex items-center gap-1.5"><Landmark className="w-3.5 h-3.5" /> {n.buyer}</p>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
        <span className="text-xs text-slate-400">CPV {n.cpv || "—"}</span>
        {n.url && <a href={n.url} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-primary flex items-center gap-1" data-testid="ted-link">Voir sur TED <ExternalLink className="w-3.5 h-3.5" /></a>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("today");
  const [feed, setFeed] = useState(null);
  const [favorites, setFavorites] = useState(null);
  const [agenda, setAgenda] = useState(null);
  const [buyers, setBuyers] = useState(null);
  const [buyersLoading, setBuyersLoading] = useState(false);
  const [ted, setTed] = useState(null);
  const [tedLoading, setTedLoading] = useState(false);
  const [tedLocked, setTedLocked] = useState(false);
  const [tracked, setTracked] = useState(null);
  const [trackFilter, setTrackFilter] = useState("all");
  const [roi, setRoi] = useState(null);
  const [goalEdit, setGoalEdit] = useState(false);
  const [goalInput, setGoalInput] = useState("");
  const [draftBuyer, setDraftBuyer] = useState(null);
  const [draftText, setDraftText] = useState("");
  const [draftLoading, setDraftLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [sending, setSending] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const isAdmin = (user?.email || "").toLowerCase() === "freddy.boussie@gmail.com";

  const loadFeed = useCallback(() => {
    setLoading(true);
    api.get("/tenders/feed").then(({ data }) => { setFeed(data); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  const loadFav = useCallback(() => api.get("/favorites").then(({ data }) => setFavorites(data)).catch(() => {}), []);
  const loadAgenda = useCallback(() => api.get("/agenda").then(({ data }) => setAgenda(data)).catch(() => {}), []);
  const loadRoi = useCallback(() => api.get("/stats/roi").then(({ data }) => setRoi(data)).catch(() => {}), []);
  const loadBuyers = useCallback(() => {
    setBuyersLoading(true);
    api.get("/sources/buyers").then(({ data }) => { setBuyers(data.buyers); setBuyersLoading(false); }).catch(() => setBuyersLoading(false));
  }, []);
  const loadTed = useCallback(() => {
    setTedLoading(true);
    api.get("/sources/ted").then(({ data }) => { setTed(data.notices || []); setTedLocked(false); setTedLoading(false); })
      .catch((e) => { if (e?.response?.status === 403) setTedLocked(true); setTed([]); setTedLoading(false); });
  }, []);
  const loadTracked = useCallback(() => api.get("/tracking").then(({ data }) => setTracked(data.tenders || [])).catch(() => setTracked([])), []);

  useEffect(() => { loadFeed(); loadRoi(); }, [loadFeed, loadRoi]);
  useEffect(() => {
    if (tab === "favorites") loadFav();
    if (tab === "agenda") loadAgenda();
    if (tab === "tracking") loadTracked();
    if (tab === "buyers" && buyers === null) loadBuyers();
    if (tab === "ted" && ted === null) loadTed();
  }, [tab, loadFav, loadAgenda, loadTracked, loadBuyers, loadTed, buyers, ted]);

  const refreshAll = () => { loadFeed(); loadFav(); loadAgenda(); loadRoi(); loadTracked(); if (tab === "buyers") loadBuyers(); };

  const saveGoal = async () => {
    try {
      await api.post("/stats/goal", { amount: Number(goalInput) || 0 });
      setGoalEdit(false);
      loadRoi();
      toast.success("Objectif mis à jour");
    } catch { toast.error("Impossible d'enregistrer l'objectif."); }
  };

  const openContact = async (b) => {
    setDraftBuyer(b); setDraftText(""); setDraftLoading(true);
    try {
      const { data } = await api.post("/sources/buyers/draft", {
        objet: b.objet, acheteur_nom: b.acheteur_nom, montant: b.montant, departement: b.departement,
      });
      setDraftText(data.draft);
    } catch { toast.error("Génération du brouillon indisponible."); }
    finally { setDraftLoading(false); }
  };

  const copyDraft = () => {
    navigator.clipboard?.writeText(draftText).then(() => toast.success("Brouillon copié"));
  };
  const mailtoHref = `mailto:?subject=${encodeURIComponent("Présentation de notre entreprise")}&body=${encodeURIComponent(draftText)}`;

  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/alerts/send-test");
      if (data.sent) toast.success(`Email envoyé à ${data.to} (${data.count} marché(s))`);
      else toast.info(data.message || "Aucun marché pertinent pour le moment.");
    } catch (e) { toast.error("Envoi impossible pour le moment."); }
    finally { setSending(false); }
  };

  const planLabel = { trial: "Essai gratuit", trialing: "Essai gratuit", none: "Sans formule", essentiel: "Essentiel", pro: "Pro", premium: "Premium" }[user?.plan] || "Essai";
  const PLAN_PRICE = { essentiel: 29, pro: 59, premium: 149 };
  const trialEnd = user?.plan === "trialing" ? user?.trial_ends_at : user?.current_period_end;
  const showSubBanner = (user?.plan === "trialing" || user?.cancel_at_period_end) && trialEnd;
  const TABS = [
    { k: "today", label: "Du jour", icon: Inbox },
    { k: "favorites", label: "Favoris", icon: Heart },
    { k: "agenda", label: "Agenda", icon: CalendarDays },
    { k: "tracking", label: "Mon suivi", icon: ListChecks },
    { k: "buyers", label: "Sous 90 k€", icon: Building2 },
    { k: "ted", label: "Grands marchés", icon: Globe },
  ];

  const list = tab === "today" ? feed?.tenders : tab === "favorites" ? favorites?.tenders : agenda?.tenders;
  const trackedList = (tracked || [])
    .filter((t) => trackFilter === "all" || t.user_status === trackFilter)
    .sort((a, b) => {
      const da = a.date_limite || "9999-12-31";
      const dbb = b.date_limite || "9999-12-31";
      return da < dbb ? -1 : da > dbb ? 1 : 0;
    });
  const trackCounts = {
    all: (tracked || []).length,
    interested: (tracked || []).filter((t) => t.user_status === "interested").length,
    applied: (tracked || []).filter((t) => t.user_status === "applied").length,
    won: (tracked || []).filter((t) => t.user_status === "won").length,
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 bg-white border-b border-border">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-xs font-mono font-bold text-primary bg-blue-50 border border-blue-200 px-2.5 py-1 rounded">{planLabel}</span>
            <Button variant="ghost" size="sm" onClick={() => navigate("/reglages")} data-testid="go-settings-btn"><Settings className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Réglages</span></Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/abonnement")} data-testid="go-billing-btn"><CreditCard className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Abonnement</span></Button>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="dashboard-logout-btn"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8">
        {showSubBanner && (
          <div className={`mb-6 rounded-2xl border p-4 flex flex-wrap items-center gap-3 ${user?.cancel_at_period_end ? "border-slate-300 bg-slate-50" : "border-amber-300 bg-amber-50"}`} data-testid="trial-banner">
            <Clock className={`w-5 h-5 shrink-0 ${user?.cancel_at_period_end ? "text-slate-400" : "text-amber-500"}`} />
            <p className="text-sm text-slate-700 flex-1 min-w-[220px]">
              {user?.cancel_at_period_end ? (
                <>Abonnement résilié. Votre accès reste actif jusqu'au <b>{fmtDate(trialEnd)}</b> — aucun autre prélèvement ne sera effectué.</>
              ) : (
                <>Essai gratuit en cours. Il se termine le <b>{fmtDate(trialEnd)}</b> : sans résiliation, vous serez alors facturé <b>{PLAN_PRICE[user?.chosen_plan] ?? ""} €/mois</b> ({planLabel === "Essai gratuit" ? (user?.chosen_plan || "") : planLabel}). Résiliez avant pour éviter le prélèvement.</>
              )}
            </p>
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => navigate("/abonnement")} data-testid="trial-banner-manage-btn">
              {user?.cancel_at_period_end ? "Voir mon abonnement" : "Gérer / résilier"}
            </Button>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900">Bonjour {user?.name?.split(" ")[0] || ""}</h1>
            <p className="text-slate-500 mt-1">Votre sélection, secteur <span className="font-semibold text-slate-700">{feed?.niche || user?.profile?.niche_label}</span></p>
          </div>
          <div className="grid grid-cols-2 gap-2 w-full sm:flex sm:w-auto">
            <Button variant="outline" onClick={() => setReviewOpen(true)} className="rounded-full w-full sm:w-auto" data-testid="open-review-btn"><Star className="w-4 h-4 mr-1.5" /> Donner mon avis</Button>
            <Button variant="outline" onClick={refreshAll} className="rounded-full w-full sm:w-auto" data-testid="refresh-feed-btn"><RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Actualiser</Button>
            <Button onClick={sendTest} disabled={sending} className="rounded-full w-full col-span-2 sm:col-auto sm:w-auto" data-testid="send-test-alert-btn"><Send className="w-4 h-4 mr-1.5" /> {sending ? "Envoi…" : "M'envoyer l'email"}</Button>
          </div>
        </div>

        {roi && user?.profile && (
          <div className="mb-6 rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white p-5 flex flex-wrap items-center gap-x-8 gap-y-4" data-testid="roi-banner">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0"><Trophy className="w-6 h-6" /></div>
              <div>
                <p className="text-xs font-mono uppercase tracking-wide text-emerald-700">Marchés gagnés</p>
                <p className="font-display text-2xl font-extrabold text-slate-900" data-testid="roi-won-total">{fmtEuro(roi.won_total)}</p>
              </div>
            </div>
            <div className="hidden sm:block h-10 w-px bg-emerald-200" />
            <div>
              <p className="text-xs font-mono uppercase tracking-wide text-slate-400">Remportés</p>
              <p className="font-display text-xl font-bold text-slate-900" data-testid="roi-won-count">{roi.won_count} marché{roi.won_count > 1 ? "s" : ""}</p>
            </div>
            <div>
              <p className="text-xs font-mono uppercase tracking-wide text-slate-400">Votre abonnement</p>
              <p className="font-display text-xl font-bold text-slate-900">{roi.plan_cost_monthly} €/mois</p>
            </div>
            {roi.roi ? (
              <div className="sm:ml-auto flex items-center gap-2 rounded-xl bg-emerald-600 text-white px-4 py-2" data-testid="roi-ratio">
                <TrendingUp className="w-5 h-5" />
                <span className="font-display text-xl font-extrabold">ROI ×{roi.roi}</span>
              </div>
            ) : (
              <p className="sm:ml-auto text-sm text-slate-500 max-w-xs">Marquez un marché « Marché gagné » sur sa fiche pour suivre votre retour sur investissement.</p>
            )}
            <div className="w-full pt-2" data-testid="goal-block">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs font-mono uppercase tracking-wide text-slate-400">Objectif {roi.year}</p>
                {!goalEdit ? (
                  <button onClick={() => { setGoalInput(String(Math.round(roi.annual_goal))); setGoalEdit(true); }} className="text-xs font-semibold text-primary flex items-center gap-1" data-testid="goal-edit-btn"><Pencil className="w-3 h-3" /> {fmtEuro(roi.annual_goal)}</button>
                ) : (
                  <span className="flex items-center gap-1.5">
                    <input type="number" value={goalInput} onChange={(e) => setGoalInput(e.target.value)} className="w-28 rounded-lg border border-border px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary/30" data-testid="goal-input" />
                    <button onClick={saveGoal} className="text-emerald-600 hover:text-emerald-700" data-testid="goal-save-btn"><Check className="w-4 h-4" /></button>
                  </span>
                )}
              </div>
              <div className="h-3 w-full rounded-full bg-slate-100 overflow-hidden" data-testid="goal-progress">
                <div className="h-full rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${roi.goal_progress}%` }} />
              </div>
              <p className="text-xs text-slate-500 mt-1.5">{fmtEuro(roi.won_total_year)} gagnés cette année · objectif {fmtEuro(roi.annual_goal)} ({roi.goal_progress}%)</p>
            </div>
          </div>
        )}

        {isAdmin && <AdminReviews />}

        <div className="flex gap-1 mb-6 border-b border-border overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap shrink-0 ${tab === t.k ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "today" && feed?.activity_note && !feed?.locked && !loading && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-slate-700 flex items-start gap-2" data-testid="activity-note">
            <Target className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <span>{feed.activity_note}</span>
          </div>
        )}

        {tab === "today" && loading && (
          <div className="grid md:grid-cols-2 gap-4">{[1, 2, 3, 4].map((i) => <div key={i} className="h-40 rounded-2xl bg-white border border-border animate-pulse" />)}</div>
        )}

        {tab === "today" && feed?.locked && (
          <div className="text-center py-16 bg-white rounded-2xl border border-amber-200">
            <Lock className="w-12 h-12 text-amber-400 mx-auto mb-4" />
            <p className="font-semibold text-slate-900">Choisissez vos départements</p>
            <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{feed.message}</p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center mt-5">
              <Button variant="outline" className="rounded-full" onClick={() => navigate("/reglages")} data-testid="locked-settings-btn">Choisir mes départements</Button>
              <Button className="rounded-full" onClick={() => navigate("/abonnement")} data-testid="locked-upgrade-btn">Passer en Pro</Button>
            </div>
          </div>
        )}

        {(!list || list.length === 0) && !(tab === "today" && loading) && !(tab === "today" && feed?.locked) && tab !== "buyers" && tab !== "ted" && tab !== "tracking" && (
          <div className="text-center py-20 bg-white rounded-2xl border border-border">
            <Target className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="font-semibold text-slate-900">
              {tab === "today" ? "Aucun marché pertinent pour l'instant" : tab === "favorites" ? "Aucun favori pour le moment" : "Aucune échéance à suivre"}
            </p>
            <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
              {tab === "today" ? "Les annonces sont publiées deux fois par jour. Élargissez vos départements dans les réglages ou revenez demain matin."
                : tab === "favorites" ? "Cliquez sur le cœur d'un marché pour le retrouver ici." : "Marquez un marché « Ça m'intéresse » ou ajoutez-le en favori pour suivre sa date limite."}
            </p>
            {tab === "today" && <Button variant="outline" className="mt-5 rounded-full" onClick={() => navigate("/reglages")}>Ajuster mes réglages</Button>}
          </div>
        )}

        {tab === "agenda" && agenda?.tenders?.length > 0 && (
          <CalendarView tenders={agenda.tenders} onOpen={setOpen} />
        )}

        {tab === "buyers" && (
          <>
            <div className="mb-5 rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-slate-600 leading-relaxed" data-testid="buyers-intro">
              <p className="font-semibold text-slate-800 mb-1 flex items-center gap-1.5"><Building2 className="w-4 h-4 text-primary" /> Marchés sous 90 k€ près de chez vous</p>
              Ces marchés viennent d'être notifiés par des acheteurs publics de votre secteur. Beaucoup ne passent jamais par le BOAMP : identifiez les acheteurs actifs et positionnez-vous sur leurs prochains besoins.
              <span className="block mt-1.5 text-xs text-slate-400">Source : DECP — data.economie.gouv.fr, Licence Ouverte / Etalab 2.0.</span>
            </div>
            {buyersLoading && (
              <div className="grid md:grid-cols-2 gap-4">{[1, 2, 3, 4].map((i) => <div key={i} className="h-40 rounded-2xl bg-white border border-border animate-pulse" />)}</div>
            )}
            {!buyersLoading && buyers && buyers.length > 0 && (
              <div className="grid md:grid-cols-2 gap-4">
                {buyers.map((b, i) => <BuyerCard key={`${b.acheteur_id}-${i}`} b={b} onContact={openContact} />)}
              </div>
            )}
            {!buyersLoading && buyers && buyers.length === 0 && (
              <div className="text-center py-20 bg-white rounded-2xl border border-border">
                <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="font-semibold text-slate-900">Aucun marché sous 90 k€ pour l'instant</p>
                <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">Élargissez vos départements dans les réglages : ces petits marchés sont publiés de façon irrégulière par les acheteurs.</p>
              </div>
            )}
          </>
        )}

        {tab === "ted" && (
          <>
            <div className="mb-5 rounded-xl border border-indigo-100 bg-indigo-50 p-4 text-sm text-slate-600 leading-relaxed" data-testid="ted-intro">
              <p className="font-semibold text-slate-800 mb-1 flex items-center gap-1.5"><Globe className="w-4 h-4 text-primary" /> Grands marchés européens</p>
              Les marchés publics français d'ampleur, publiés au niveau européen, avec leur montant quand il est publié. Pour viser plus haut.
              <span className="block mt-1.5 text-xs text-slate-400">Source : TED — Tenders Electronic Daily (Office des publications de l'Union européenne).</span>
            </div>
            {tedLocked && (
              <div className="text-center py-16 bg-white rounded-2xl border border-amber-200" data-testid="ted-locked">
                <Lock className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <p className="font-semibold text-slate-900">Disponible à partir de la formule Pro</p>
                <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">Les grands marchés européens (TED) sont inclus dans les formules Pro et Premium.</p>
                <Button className="rounded-full mt-5" onClick={() => navigate("/abonnement")} data-testid="ted-upgrade-btn">Passer en Pro</Button>
              </div>
            )}
            {!tedLocked && tedLoading && (
              <div className="grid md:grid-cols-2 gap-4">{[1, 2, 3, 4].map((i) => <div key={i} className="h-40 rounded-2xl bg-white border border-border animate-pulse" />)}</div>
            )}
            {!tedLocked && !tedLoading && ted && ted.length > 0 && (
              <div className="grid md:grid-cols-2 gap-4">{ted.map((n, i) => <TedCard key={n.publication_number || i} n={n} />)}</div>
            )}
            {!tedLocked && !tedLoading && ted && ted.length === 0 && (
              <div className="text-center py-20 bg-white rounded-2xl border border-border">
                <Globe className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="font-semibold text-slate-900">Aucun grand marché européen pour l'instant</p>
                <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">Ces marchés d'ampleur sont plus rares ; revenez régulièrement.</p>
              </div>
            )}
          </>
        )}

        {tab === "tracking" && (
          <>
            <div className="flex flex-wrap gap-2 mb-5" data-testid="tracking-filters">
              {[{ k: "all", l: "Tous" }, { k: "interested", l: "Ça m'intéresse" }, { k: "applied", l: "J'ai candidaté" }, { k: "won", l: "Marché gagné" }].map((f) => (
                <button key={f.k} onClick={() => setTrackFilter(f.k)} data-testid={`track-filter-${f.k}`}
                  className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${trackFilter === f.k ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-border hover:border-slate-300"}`}>
                  {f.l} <span className={`ml-1 ${trackFilter === f.k ? "text-white/80" : "text-slate-400"}`}>({trackCounts[f.k]})</span>
                </button>
              ))}
            </div>
            {tracked === null ? (
              <div className="grid md:grid-cols-2 gap-4">{[1, 2, 3, 4].map((i) => <div key={i} className="h-40 rounded-2xl bg-white border border-border animate-pulse" />)}</div>
            ) : trackedList.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">{trackedList.map((t) => <TenderCard key={t.idweb} t={t} onOpen={setOpen} />)}</div>
            ) : (
              <div className="text-center py-20 bg-white rounded-2xl border border-border" data-testid="tracking-empty">
                <ListChecks className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="font-semibold text-slate-900">Aucun marché dans cette catégorie</p>
                <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">Ouvrez la fiche d'un marché et marquez-le « Ça m'intéresse », « J'ai candidaté » ou « Marché gagné » pour le retrouver ici.</p>
              </div>
            )}
          </>
        )}

        {tab !== "agenda" && tab !== "buyers" && tab !== "ted" && tab !== "tracking" && list && list.length > 0 && (
          <div className="grid md:grid-cols-2 gap-4">
            {list.map((t) => <TenderCard key={t.idweb} t={t} onOpen={setOpen} />)}
          </div>
        )}
      </main>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        {open && <TenderModal idweb={open} onClose={() => setOpen(null)} onChange={refreshAll} />}
      </Dialog>

      <Dialog open={!!draftBuyer} onOpenChange={(v) => !v && setDraftBuyer(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="buyer-draft-modal">
          <DialogTitle>Email de présentation</DialogTitle>
          <DialogDescription>Brouillon personnalisé à relire et compléter avant envoi à {draftBuyer?.acheteur_nom}. Les parties entre [crochets] sont à compléter.</DialogDescription>
          {draftLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-9 h-9 border-2 border-primary border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-sm text-slate-500 font-mono">Nous rédigeons votre brouillon…</p>
            </div>
          ) : (
            <>
              <Textarea value={draftText} onChange={(e) => setDraftText(e.target.value)} rows={14} className="font-mono text-sm leading-relaxed" data-testid="buyer-draft-text" />
              <div className="flex flex-wrap gap-2 justify-end">
                <Button variant="outline" className="rounded-full" onClick={copyDraft} data-testid="buyer-draft-copy-btn"><Copy className="w-4 h-4 mr-1.5" /> Copier</Button>
                <a href={mailtoHref} data-testid="buyer-draft-mailto-btn"><Button className="rounded-full"><Mail className="w-4 h-4 mr-1.5" /> Ouvrir dans ma messagerie</Button></a>
              </div>
              <p className="text-xs text-slate-400">Brouillon généré pour vous aider : vérifiez le contenu, il n'engage que vous une fois envoyé.</p>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ReviewDialog open={reviewOpen} onClose={() => setReviewOpen(false)} />
    </div>
  );
}
